import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import { GENERATING_REPORT } from "../../../constants/loading.constant";
import { useAnalyticsCore } from "../../../cores/analytics";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import {
  collectSummaryReportSections,
  downloadPdfFromSections,
  getReportDomId,
  setSummaryCaptureMode,
} from "../../utils/reportCapture";
import { UpgradePlanButton } from "../plan/UpgradePlanButton";

interface Props {
  summarySelected?: boolean;
  summaryLoading?: boolean;
  summaryReady?: boolean;
}

export const ActionButtons = ({
  summarySelected = false,
  summaryLoading = false,
  summaryReady = false,
}: Props) => {
  const intl = useIntl();
  const { turnOnLoading, turnOffLoading } = useGlobalCore();
  const { company, companyPlan, updateReportsCompany } = useMeCore();
  const { socialProviders, getInsightReport } = useAnalyticsCore();

  const canDownload = useMemo(() => {
    if (moment().isSame(moment(company?.lastReportDate), "M")) {
      return company?.reports! < companyPlan?.rules.reportsAmount!;
    }
    return true;
  }, [companyPlan, company]);

  const handleDownloadReport = useCallback(async () => {
    if (!company?.id || (socialProviders.length === 0 && !summarySelected)) {
      return;
    }

    turnOnLoading(GENERATING_REPORT);

    try {
      await getInsightReport(company.id, async () => {
        const sections: { element: HTMLElement }[] = [];

        if (summarySelected) {
          await setSummaryCaptureMode(true);
          sections.push(...collectSummaryReportSections());
        }

        for (const social of socialProviders) {
          const header = document.getElementById(
            getReportDomId(social.id, "Header")
          );
          const graphs = document.querySelectorAll(
            `[id='${getReportDomId(social.id, "Chart")}']`
          );

          if (!header || graphs.length === 0) continue;

          sections.push({ element: header });
          graphs.forEach((graph) => {
            sections.push({ element: graph as HTMLElement });
          });
        }

        if (!sections.length) {
          throw new Error("No report sections found");
        }

        await downloadPdfFromSections(sections, "wembii-report.pdf");
        updateReportsCompany(company.reports! + 1);
      });
    } finally {
      await setSummaryCaptureMode(false);
      turnOffLoading(GENERATING_REPORT, 500);
    }
  }, [
    socialProviders,
    summarySelected,
    company,
    updateReportsCompany,
    getInsightReport,
    turnOnLoading,
    turnOffLoading,
  ]);

  const summaryBlocked =
    summarySelected && (summaryLoading || !summaryReady);
  const disabled =
    (socialProviders.length === 0 && !summarySelected) || summaryBlocked;

  return (
    <div className="d-flex justify-content-center mt-3">
      {!canDownload ? (
        <UpgradePlanButton />
      ) : (
        <Button color="primary" disabled={disabled} onClick={handleDownloadReport}>
          <FontAwesomeIcon
            icon={["far", "file-pdf"]}
            className="mx-2"
            size="1x"
            color="white"
          />
          {intl.formatMessage({ id: "app.reports.downloadReport" })}
        </Button>
      )}
    </div>
  );
};
